/**
 * Compressão de prints no navegador, antes do envio: um print de celular
 * (PNG de 2 a 4 MB) vira um JPEG de algumas centenas de KB, ainda legível —
 * e o banco não incha. PDFs e imagens já pequenas passam direto.
 */
const MAX_DIMENSION = 2000; // px no maior lado: o texto do print continua nítido
const QUALITY = 0.85;
const SMALL_ENOUGH = 300 * 1024; // abaixo disso, recomprimir não compensa

export async function compressImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.size <= SMALL_ENOUGH) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // formato que este navegador não decodifica: o servidor decide
  }

  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return file;
  }

  // Fundo branco: a transparência de um PNG ficaria preta no JPEG.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
  if (!blob || blob.size >= file.size) return file;

  const name = `${file.name.replace(/\.[^.]*$/, '') || 'comprovante'}.jpg`;
  return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
}

/** Lado da foto de perfil, em px — cobre o maior avatar da tela em telas de alta densidade. */
const PHOTO_SIDE = 256;
const PHOTO_QUALITY = 0.88;

/**
 * Foto de perfil pronta para enviar: o centro da imagem recortado num
 * quadrado e reduzido a 256 px, em JPEG. Fica com dezenas de KB e sem os
 * metadados da original — como a localização de uma foto de celular.
 */
export async function preparePhoto(file: File): Promise<File> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('Não foi possível ler esta imagem. Escolha um JPEG, PNG ou WEBP.');
  }

  const crop = Math.min(bitmap.width, bitmap.height);
  const side = Math.min(PHOTO_SIDE, crop);
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    throw new Error('Este navegador não conseguiu preparar a foto. Tente em outro.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, side, side);
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, (bitmap.width - crop) / 2, (bitmap.height - crop) / 2, crop, crop, 0, 0, side, side);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY));
  if (!blob) throw new Error('Este navegador não conseguiu preparar a foto. Tente em outro.');
  return new File([blob], 'foto.jpg', { type: 'image/jpeg', lastModified: Date.now() });
}
